import React from 'react';
import { useTranslation } from 'react-i18next';
import { ConsentDecision, setConsent } from '../../utils/consent';
import { CommonMessages } from '../../i18n/Common.messages';
import { ConsentMessages, ConsentText } from './ConsentBanner.messages';
import './ConsentBanner.css';

/**
 * Accept / decline banner for analytics and advertising cookies.
 *
 * This component renders the same markup on every page load and never reads
 * `localStorage`, `window` or the current consent state while rendering. Whether
 * the banner is *shown* is decided entirely by the `data-consent` attribute the
 * inline script in public/index.html puts on <html> before the first paint, and
 * the matching rule in that file's <style> block.
 *
 * That indirection is what keeps it honest on a prerendered site. 232 pages are
 * snapshotted by puppeteer against an empty localStorage, so every snapshot
 * contains this markup whether the visitor has decided or not. The alternatives
 * both fail:
 *
 *  - deciding visibility from state during render disagrees with the snapshot and
 *    costs the page its hydration;
 *  - revealing it from an effect means a returning visitor who already declined
 *    watches the banner appear and then vanish once main.js executes.
 *
 * Resolving it from an attribute set during HTML parse means the banner is either
 * painted or not painted, once, with nothing to correct afterwards — the same
 * trick the theme script uses, for the same reason.
 */
export const ConsentBanner: React.FC = () => {
    const { t } = useTranslation();

    const decide = (decision: ConsentDecision) => () => {
        // Writes the attribute this banner's own visibility depends on, so there
        // is no React state to keep in step and no re-render to wait for.
        setConsent(decision);
    };

    return (
        <div
            id="localingos-consent-banner"
            role="region"
            aria-label={t(ConsentMessages.REGION_LABEL, { defaultValue: ConsentText.REGION_LABEL })}
        >
            <div className="consent-banner-copy">
                <p className="consent-banner-title">
                    {t(ConsentMessages.TITLE, { defaultValue: ConsentText.TITLE })}
                </p>
                <p className="consent-banner-body">
                    {t(ConsentMessages.BODY, { defaultValue: ConsentText.BODY })}{' '}
                    <a className="consent-banner-link" href="/privacy">
                        {t(CommonMessages.PRIVACY_POLICY)}
                    </a>
                </p>
            </div>
            <div className="consent-banner-actions">
                <button type="button" className="consent-banner-button" onClick={decide('denied')}>
                    {t(ConsentMessages.DECLINE, { defaultValue: ConsentText.DECLINE })}
                </button>
                <button type="button" className="consent-banner-button" onClick={decide('granted')}>
                    {t(ConsentMessages.ACCEPT, { defaultValue: ConsentText.ACCEPT })}
                </button>
            </div>
        </div>
    );
};
